// Workspace shell — every turn becomes a card group on a freeform canvas.
// V1 scope: single thread (multi-thread tabs are visual scaffolding;
// useChatState only manages one current/priorTurns pair). Drag positions
// per card, persisted to localStorage. Mini-map and follow-up pill complete
// the workspace metaphor.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Card } from './workspace/Card';
import type { CardPosition } from './workspace/Card';
import { MiniMap } from './workspace/MiniMap';
import { PresentationContainer } from '../components/presentation/PresentationContainer';
import { safeUrl } from '../safeUrl';
import type { ShellProps } from './types';
import type { Citation, Presentation, Turn } from '../types';

const POSITIONS_KEY = 'hrsa.workspace.positions';

// Stable per-turn synthetic id: priorTurns are indexed 0..N-1, current is "C".
function turnId(i: number | 'C'): string {
  return `t-${i}`;
}

function loadPositions(): Record<string, CardPosition> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(POSITIONS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') return parsed as Record<string, CardPosition>;
  } catch {
    // Fall through to empty positions on bad/missing data.
  }
  return {};
}

function savePositions(p: Record<string, CardPosition>) {
  try { window.localStorage.setItem(POSITIONS_KEY, JSON.stringify(p)); } catch { /* best-effort */ }
}

interface CardSpec {
  id: string;
  position: CardPosition;
  kind: 'question' | 'standard';
  title?: string;
  presPill?: string;
  sourceCount?: number;
  render: () => React.ReactNode;
}

function defaultPosition(turnIdx: number, slot: 'question' | 'pres' | 'sources' | 'suggest'): CardPosition {
  // Each turn gets its own row band. 4 cards per row max; wrap horizontally.
  const ROW_HEIGHT = 360;
  const rowTop = 24 + turnIdx * ROW_HEIGHT;
  switch (slot) {
    case 'question': return { x: 32, y: rowTop, w: 520, h: 88 };
    case 'pres':     return { x: 32, y: rowTop + 104, w: 520, h: 240 };
    case 'sources':  return { x: 572, y: rowTop + 104, w: 380, h: 240 };
    case 'suggest':  return { x: 972, y: rowTop + 104, w: 280, h: 240 };
  }
}

interface RenderCardArgs {
  turnIdx: number | 'C';
  query: string;
  presentation: Presentation | null;
  prose: string;
  citations: Citation[];
  toolsUsed: string[];
  streaming: boolean;
}

function buildCards(turnArgs: RenderCardArgs[], positions: Record<string, CardPosition>): CardSpec[] {
  const cards: CardSpec[] = [];
  turnArgs.forEach((t, idx) => {
    const idIdx = t.turnIdx;
    const baseId = turnId(idIdx);
    cards.push({
      id: `${baseId}-q`,
      position: positions[`${baseId}-q`] ?? defaultPosition(idx, 'question'),
      kind: 'question',
      render: () => (
        <>
          <div className="ws-q-kicker">QUESTION</div>
          <div className="ws-q-text">{t.query}</div>
          <div className="ws-q-meta">
            {t.toolsUsed.map((tool) => (
              <span key={tool} className="ws-q-tool-chip">{tool}</span>
            ))}
            {t.streaming && <span className="ws-q-elapsed">streaming…</span>}
          </div>
        </>
      ),
    });
    if (t.presentation) {
      cards.push({
        id: `${baseId}-pres`,
        position: positions[`${baseId}-pres`] ?? defaultPosition(idx, 'pres'),
        kind: 'standard',
        title: 'Result',
        presPill: t.presentation.type,
        render: () => (
          <>
            <PresentationContainer p={t.presentation as Presentation} />
            {t.prose && (
              <div className={`prose ${t.streaming ? 'prose--streaming' : ''}`} style={{ marginTop: 8, fontSize: 12 }} aria-live="polite">
                {t.streaming ? t.prose : <ReactMarkdown>{t.prose}</ReactMarkdown>}
              </div>
            )}
          </>
        ),
      });
    } else if (t.prose) {
      cards.push({
        id: `${baseId}-pres`,
        position: positions[`${baseId}-pres`] ?? defaultPosition(idx, 'pres'),
        kind: 'standard',
        title: 'Answer',
        presPill: 'prose',
        render: () => (
          <div className={`prose ${t.streaming ? 'prose--streaming' : ''}`} aria-live="polite" aria-busy={t.streaming}>
            {t.streaming ? <>{t.prose}<span className="prose-cursor" aria-hidden="true">▍</span></> : <ReactMarkdown>{t.prose}</ReactMarkdown>}
          </div>
        ),
      });
    }
    if (t.citations.length > 0) {
      cards.push({
        id: `${baseId}-src`,
        position: positions[`${baseId}-src`] ?? defaultPosition(idx, 'sources'),
        kind: 'standard',
        title: 'Sources',
        sourceCount: t.citations.length,
        render: () => (
          <div className="ws-src-grid">
            {t.citations.map((c) => (
              <div key={c.index} className="ws-src-row">
                <span className="ws-src-n">[{c.index}]</span>
                <div className="ws-src-body">
                  <div className="ws-src-lbl">{c.label}</div>
                  <a className="ws-src-url" href={safeUrl(c.url)} target="_blank" rel="noopener noreferrer">{c.url} ↗</a>
                </div>
              </div>
            ))}
          </div>
        ),
      });
    }
  });
  return cards;
}

const FOLLOWUP_SUGGESTIONS = [
  'Compare with neighboring states',
  'Limit to rural counties',
  'Show the demographic breakdown',
];

interface RenderArgs {
  current: ShellProps['current'];
  priorTurns: Turn[];
}

function gatherTurns({ current, priorTurns }: RenderArgs): RenderCardArgs[] {
  const out: RenderCardArgs[] = priorTurns.map((t, i) => ({
    turnIdx: i,
    query: t.user_query,
    presentation: null,           // priorTurns lose their presentation envelope on the wire
    prose: t.assistant_summary,
    citations: [],
    toolsUsed: [],
    streaming: false,
  }));
  if (current) {
    out.push({
      turnIdx: 'C',
      query: current.query,
      presentation: current.assistant.presentation ?? null,
      prose: current.assistant.prose,
      citations: current.assistant.citations,
      toolsUsed: current.assistant.toolsUsed,
      streaming: current.streaming,
    });
  }
  return out;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export function WorkspaceShell({ current, priorTurns, submit }: ShellProps) {
  const [positions, setPositions] = useState<Record<string, CardPosition>>(() => loadPositions());
  const [searchValue, setSearchValue] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const isLinear = useMediaQuery('(max-width: 1279px)');

  // ⌘K focuses the search input (consistent across all shells per the plan).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const turns = useMemo(() => gatherTurns({ current, priorTurns }), [current, priorTurns]);
  const cards = useMemo(() => buildCards(turns, positions), [turns, positions]);

  const onMove = useCallback((id: string, dx: number, dy: number) => {
    setPositions((prev) => {
      const cur = prev[id] ?? cards.find((c) => c.id === id)?.position;
      if (!cur) return prev;
      const next = {
        ...prev,
        [id]: {
          ...cur,
          x: Math.max(0, cur.x + dx),
          y: Math.max(0, cur.y + dy),
        },
      };
      savePositions(next);
      return next;
    });
  }, [cards]);

  const onSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const q = searchValue.trim();
    if (q) submit(q);
    setSearchValue('');
  }, [searchValue, submit]);

  const onJumpTo = useCallback((id: string) => {
    const card = cards.find((c) => c.id === id);
    if (!card || !canvasRef.current) return;
    canvasRef.current.scrollTo({ left: card.position.x - 32, top: card.position.y - 32, behavior: 'smooth' });
  }, [cards]);

  // Compute canvas bounds so the mini-map can scale.
  const canvasW = Math.max(1280, ...cards.map((c) => c.position.x + c.position.w + 32));
  const canvasH = Math.max(720, ...cards.map((c) => c.position.y + c.position.h + 32));

  const minimapPositions = cards.map((c) => ({ id: c.id, pos: c.position }));
  const tabLabel = current ? current.query.slice(0, 40) : (priorTurns[priorTurns.length - 1]?.user_query.slice(0, 40) ?? 'Workspace');

  return (
    <div className="ws-root">
      <div className="ws-topbar">
        <div className="brand brand--compact">
          <div className="brand-mark">HDW</div>
          <span className="brand-title">Workspace</span>
        </div>
        <form className="ws-search" onSubmit={onSubmit}>
          <span className="ws-search__icon" aria-hidden="true">⌕</span>
          <input
            ref={searchRef}
            className="ws-search__input"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Ask anything — e.g. counties with HPSA + MUA but no health center"
            aria-label="Ask a question"
            disabled={current?.streaming ?? false}
          />
          <span className="ws-search__kbd" aria-hidden="true">⌘K</span>
        </form>
        <div className="ws-topbar__right">
          <button className="ws-topbar__icon-btn" type="button" onClick={() => alert('Share coming soon')}>Share</button>
        </div>
      </div>

      <div className="ws-subbar">
        <div className="ws-tab-row">
          <button className="ws-tab ws-tab--active">
            <span className="ws-tab__dot" aria-hidden="true" />
            {tabLabel}
            <span className="ws-tab__count">{cards.length} cards</span>
          </button>
          <button className="ws-tab-add" type="button" disabled aria-label="New thread (coming soon)">+</button>
        </div>
        <div>
          <span className="ws-subbar__meta">{cards.length === 0 ? 'Empty workspace' : `${cards.length} cards · auto-saved`}</span>
        </div>
      </div>

      <div ref={canvasRef} className={`ws-canvas ${isLinear ? 'ws-canvas--linear' : ''}`}>
        {!isLinear && <div className="ws-grid-bg" aria-hidden="true" />}
        {cards.length === 0 && (
          <div style={{ padding: 64, textAlign: 'center', color: 'var(--gray-500)' }}>
            Ask a question above to start your workspace.
          </div>
        )}
        {cards.map((c) => (
          <Card
            key={c.id}
            id={c.id}
            position={c.position}
            onMove={onMove}
            draggable={!isLinear}
            kind={c.kind}
            title={c.title}
            presPill={c.presPill}
            sourceCount={c.sourceCount}
          >
            {c.render()}
          </Card>
        ))}
        {/* Spacer to keep canvas scrollable to bounds */}
        {!isLinear && <div style={{ position: 'absolute', left: canvasW - 1, top: canvasH - 1, width: 1, height: 1 }} aria-hidden="true" />}
      </div>

      {!isLinear && cards.length > 0 && (
        <MiniMap positions={minimapPositions} canvasW={canvasW} canvasH={canvasH} onJumpTo={onJumpTo} />
      )}

      <button className="ws-followup" type="button" onClick={() => searchRef.current?.focus()}>
        <span className="ws-followup__kbd">⌘K</span>
        <span className="ws-followup__text">{FOLLOWUP_SUGGESTIONS[0]}…</span>
      </button>
    </div>
  );
}
